import { useRef } from 'react'

import {
  Box,
  Icon,
  InputCheckBox,
  NumberBadge,
  Table,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { useMemo } from 'modules/core/hooks/useMemo'
import { InjectHoverProvider } from 'modules/core/lib/hoverContext'
import i18n from 'modules/core/lib/i18n'
import type { Access } from 'modules/core/store/models/Access'

import { validate } from 'modules/payroll_bundle/components/PayrollSummaryImport/PayrollSummaryImportModal/ImportSummaryWizard/_steps/reviewAndImportStep/EmployeeMatcher/_Table/RowValidations'
import { ImportData, MatchReason } from 'modules/payroll_bundle/lib/PayrollResultContext'
import { PAYROLL_RESULT } from 'modules/payroll_bundle/lib/constants'
import { formatAmount } from 'modules/payroll_bundle/lib/formatNumber'
import {
  EMPLOYEE_MATCH_SELECTOR,
  FULL_NAME_COLUMN_TOKEN_WIDTH,
  FULL_NAME_COLUMN_WIDTH,
} from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/ResultsPage/constants'

import Amount from './Amount'
import AssignEmployeeSelector from './AssignEmployeeSelector'

interface Props {
  index: string
  selected?: Set<string>
  checkable?: boolean
  importedEmployee: ImportData
  getUserFromImportData: (item: ImportData) => Access | null | undefined
  onAssignEmployee?: (userId: string, index: string) => void
  onUnAssignEmployee?: (userId: string, index: string) => void
  onSelectItem?: (resultItem: ImportData) => void
  isSelected?: boolean
  currency: string
}

const matchReasonText = (reason: MatchReason) => {
  switch (reason) {
    case MatchReason.name:
      return i18n.t('payroll_bundle.payroll_results.match_reason.name')
    case MatchReason.identifier:
      return i18n.t('payroll_bundle.payroll_results.match_reason.id')
    case MatchReason.database:
      return i18n.t('payroll_bundle.payroll_results.match_reason.db')
    case MatchReason.company_identifier:
      return i18n.t('payroll_bundle.payroll_results.match_reason.company_identifier')
    default:
      return ''
  }
}

const Item = ({
  checkable,
  importedEmployee,
  getUserFromImportData,
  onSelectItem,
  onAssignEmployee,
  onUnAssignEmployee,
  isSelected,
  currency,
}: Props) => {
  const [open, toggle] = useToggle(false)
  const ref = useRef<HTMLParagraphElement>(null)
  const user = getUserFromImportData(importedEmployee)

  const errors = validate(importedEmployee)
  const errorCount = Object.keys(errors).length
  const isOverflowing = ref.current ? ref.current.offsetWidth < ref.current.scrollWidth : false

  const formattedGrossSalary = useMemo(() => {
    return importedEmployee.gross_salary
      ? formatAmount(Number(importedEmployee.gross_salary), currency)
      : ''
  }, [importedEmployee.gross_salary, currency])

  const formattedNetSalary = useMemo(() => {
    return importedEmployee.net_salary
      ? formatAmount(Number(importedEmployee.net_salary), currency)
      : ''
  }, [importedEmployee.net_salary, currency])

  const formattedCompanyCosts = useMemo(() => {
    return importedEmployee.company_costs
      ? formatAmount(Number(importedEmployee.company_costs), currency)
      : ''
  }, [importedEmployee.company_costs, currency])

  const handleSelectItem = () => {
    if (onSelectItem) onSelectItem(importedEmployee)
  }

  return (
    <Table.Row>
      {checkable && (
        <Table.Cell width={16} verticalAlign="middle">
          <Box>
            {errorCount > 0 ? (
              <NumberBadge label={errorCount} color="radicalRed1200" textColor="grey000White" />
            ) : (
              errorCount === 0 &&
              user && <InputCheckBox checked={!!isSelected} onClick={handleSelectItem} />
            )}
          </Box>
        </Table.Cell>
      )}
      {/* Name */}
      <Table.Cell paddingX="s16" width={FULL_NAME_COLUMN_WIDTH}>
        <Box
          flexDirection="row"
          gap="s24"
          alignItems="center"
          justifyContent="flexStart"
          maxWidth={FULL_NAME_COLUMN_TOKEN_WIDTH}
          height="full"
        >
          {!isOverflowing ? (
            <Text.Normal ellipsis weight="medium" color="grey1000" innerRef={ref}>
              {importedEmployee.full_name}
            </Text.Normal>
          ) : (
            <Tooltip.Alt
              attachment="top left"
              targetAttachment="middle right"
              size="small"
              open={isOverflowing && open}
              toggle={toggle}
              label={importedEmployee.full_name}
            >
              <InjectHoverProvider>
                <Text.Normal ellipsis weight="medium" color="grey1000" innerRef={ref}>
                  {importedEmployee.full_name}
                </Text.Normal>
              </InjectHoverProvider>
            </Tooltip.Alt>
          )}

          {importedEmployee?.match_reason && importedEmployee.match_reason !== MatchReason.none && (
            <Tooltip.Alt
              attachment="top center"
              open={open}
              toggle={toggle}
              label={matchReasonText(importedEmployee.match_reason)}
            >
              <Icon.Medium icon="info-circle-line" />
            </Tooltip.Alt>
          )}
        </Box>
      </Table.Cell>

      {/* Employee */}
      <Table.Cell paddingX="s16" width={EMPLOYEE_MATCH_SELECTOR}>
        <AssignEmployeeSelector
          user={user}
          index={importedEmployee.id}
          type={PAYROLL_RESULT}
          onAssignEmployee={onAssignEmployee}
          onUnAssignEmployee={onUnAssignEmployee}
          noMatchFormat="placeholder"
        />
      </Table.Cell>

      <Table.Cell paddingX="s16">
        <Amount
          amount={formattedGrossSalary}
          error={errors.gross_salary}
          previousValue={
            importedEmployee.current_gross_salary
              ? formatAmount(Number(importedEmployee.current_gross_salary), currency)
              : ''
          }
        />
      </Table.Cell>
      <Table.Cell paddingX="s16">
        <Amount
          amount={formattedNetSalary}
          error={errors.net_salary}
          previousValue={
            importedEmployee.current_net_salary
              ? formatAmount(Number(importedEmployee.current_net_salary), currency)
              : ''
          }
        />
      </Table.Cell>
      <Table.Cell paddingX="s16">
        <Amount
          amount={formattedCompanyCosts}
          error={errors.company_costs}
          previousValue={
            importedEmployee.current_company_costs
              ? formatAmount(Number(importedEmployee.current_company_costs), currency)
              : ''
          }
        />
      </Table.Cell>
    </Table.Row>
  )
}

export default Item
